'use client'

import { useMemo, useState } from 'react'
import Link from 'next/link'

import type { BlogsApiItem, TagApiItem } from '@/lib/api/blogs'
import { Col } from '@/components/ui/col'
import { useLocale, useTranslations } from 'next-intl'

const CHANGELOG_PAGE_SIZE = 3

type Props = {
  blogs: BlogsApiItem[]
  filterTags?: TagApiItem[]
  basePath: string
  allLabel?: string
  viewLabel: string
}

function safeDate(dateStr: string | null | undefined): Date | null {
  if (!dateStr) return null
  const d = new Date(dateStr)
  return isNaN(d.getTime()) ? null : d
}

export function InsightsEngineeringChangelog({
  blogs,
  basePath,
  viewLabel,
}: Props) {
  const locale = useLocale()
  const t = useTranslations('insightsEngineeringPage')
  const [page, setPage] = useState(0)

  const sorted = useMemo(() => {
    return (Array.isArray(blogs) ? blogs : [])
      .filter((b) => Boolean(b?.slug))
      .slice()
      .sort((a, b) => {
        const da = safeDate(a.wp_post_date)?.getTime() ?? 0
        const db = safeDate(b.wp_post_date)?.getTime() ?? 0
        return db - da
      })
  }, [blogs])

  const pageCount = Math.max(1, Math.ceil(sorted.length / CHANGELOG_PAGE_SIZE))
  const clampedPage = Math.min(page, pageCount - 1)

  const pageItems = useMemo(() => {
    const start = clampedPage * CHANGELOG_PAGE_SIZE
    return sorted.slice(start, start + CHANGELOG_PAGE_SIZE)
  }, [sorted, clampedPage])

  const canPrev = clampedPage > 0
  const canNext = clampedPage < pageCount - 1

  return (
    <div className="space-y-6">
        {pageItems.map((blog, idx) => {
          const href = `${basePath}/${blog.slug}`
          const tag = blog.tags?.[0]?.name
          const date = safeDate(blog.wp_post_date)
          const dateLabel = date
            ? date.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' })
            : null

          return (
            <article
              key={blog.documentId ?? String(blog.id)}
              className="bg-charcoal-900/50 border border-charcoal-800 p-8 card-dark-hover rounded-lg"
            >
              <Col className="md:flex-row md:items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-3 font-mono">
                  {blog.version && (
                    <span
                      className={`px-2 py-1 text-white text-fs-sm font-bold rounded-sm ${
                        idx === 0 ? 'bg-brand-red' : 'bg-charcoal-800'
                      }`}
                    >
                      {blog.version}
                    </span>
                  )}
                  {tag && (
                    <span className="px-2 py-1 bg-charcoal-950 border border-charcoal-700 text-charcoal-400 text-fs-sm rounded-sm">
                      {tag[0]?.toUpperCase() + tag?.slice(1)}
                    </span>
                  )}
                </div>
                {dateLabel && <span className="text-fs-md font-mono text-charcoal-500">{dateLabel}</span>}
              </Col>

              <h3 className="text-xl font-bold text-white mb-3 hover:text-brand-red transition-colors">
                <Link href={href}>{blog.title}</Link>
              </h3>

              {blog.short_description && (
                <p className="text-sm text-charcoal-400 font-light leading-relaxed mb-6">
                  {blog.short_description}
                </p>
              )}

              <Link
                href={href}
                className="text-fs-sm font-mono text-white uppercase tracking-widest hover:text-brand-red transition-colors flex items-center"
              >
                {viewLabel} <span className="ml-1">&rarr;</span>
              </Link>
            </article>
          )
        })}

        {pageCount > 1 && (
          <div className="flex justify-between items-center border-t border-charcoal-800 pt-8 mt-8">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={!canPrev}
              className={`px-4 py-2 border font-mono text-xs rounded-sm transition-colors ${
                canPrev
                  ? 'border-charcoal-700 text-charcoal-400 hover:text-white hover:border-charcoal-500'
                  : 'border-charcoal-700 text-charcoal-500 opacity-50 cursor-not-allowed'
              }`}
            >
              &larr; {t('changelog.next')}
            </button>

            <span className="font-mono text-xs text-charcoal-500">
              {t('changelog.page')} {clampedPage + 1} / {pageCount}
            </span>

            <button
              type="button"
              onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
              disabled={!canNext}
              className={`px-4 py-2 border font-mono text-xs rounded-sm transition-colors ${
                canNext
                  ? 'border-charcoal-700 text-charcoal-400 hover:text-white hover:border-charcoal-500'
                  : 'border-charcoal-700 text-charcoal-500 opacity-50 cursor-not-allowed'
              }`}
            >
              {t('changelog.prev')} &rarr;
            </button>
          </div>
        )}
    </div>
  )
}

